/**
 * EmptyState — used when a list or section has no data yet.
 * Icon (Lucide), heading, optional subtext, optional CTA slot.
 */
import type { ReactNode } from 'react';
import { SECTIONS, type SectionKey } from '../sections';

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  section,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  /** When set, tints the icon with the section accent. */
  section?: SectionKey;
}) {
  const sec = section ? SECTIONS[section] : null;
  return (
    <div
      className={[
        'flex flex-col items-center justify-center gap-2.5 rounded-card border border-dashed border-border bg-surface/60 px-6 py-8 text-center',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {sec ? (
        <span style={{ color: sec.color, opacity: 0.85 }}>
          <Icon className="h-9 w-9" strokeWidth={1.5} />
        </span>
      ) : (
        <Icon className="h-9 w-9 text-ink-muted opacity-50" strokeWidth={1.5} />
      )}
      <div>
        <div className="text-h2 font-semibold text-ink">{title}</div>
        {description && (
          <div className="mt-1 max-w-sm text-small text-ink-muted">{description}</div>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
